import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { authAPI } from '@/services/api/auth';
import { usersAPI } from '@/services/api/users';

export interface UserInfo {
  id: number;
  username: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  bio: string;
  department: string;
  role: 'admin' | 'publisher' | 'viewer';
  role_name: string;
  status: 'active' | 'disabled';
  status_name: string;
  created_at: string;
  updated_at: string;
  last_login_at: string;
}

interface AuthContextType {
  isLoggedIn: boolean;
  userInfo: UserInfo | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  requireAuth: () => boolean;
  refreshUserInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 刷新用户信息（从后端获取最新数据）
  const refreshUserInfo = useCallback(async () => {
    try {
      const userData = await usersAPI.getCurrentUser();
      setUserInfo(userData as UserInfo);
      setIsLoggedIn(true);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userInfo', JSON.stringify(userData));
    } catch (error) {
      console.error('刷新用户信息失败:', error);
    }
  }, []);

  // 检查登录状态
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authAPI.isAuthenticated()) {
          // 如果有token，尝试获取用户信息验证token有效性
          try {
            const userData = await authAPI.getCurrentUser();
            setIsLoggedIn(true);
            setUserInfo(userData);
            // 同步设置 localStorage 中的登录状态
            localStorage.setItem('isLoggedIn', 'true');
          } catch (error) {
            // token无效，清除本地存储
            authAPI.logout();
            setIsLoggedIn(false);
            setUserInfo(null);
            localStorage.removeItem('isLoggedIn');
          }
        } else {
          setIsLoggedIn(false);
          setUserInfo(null);
          localStorage.removeItem('isLoggedIn');
        }
      } catch (error) {
        console.error('检查登录状态失败:', error);
        setIsLoggedIn(false);
        setUserInfo(null);
        localStorage.removeItem('isLoggedIn');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 登录
  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    try {
      const response = await authAPI.login({ username, password });

      // 登录成功，设置用户信息和登录状态
      setIsLoggedIn(true);
      setUserInfo(response.user);
      // 设置 localStorage 中的登录状态，供路由保护使用
      localStorage.setItem('isLoggedIn', 'true');
      message.success('登录成功');
      navigate('/device-model');
      return true;
    } catch (error: any) {
      console.error('登录失败:', error);

      // 根据错误码显示不同的提示信息
      if (error.code === 401) {
        message.error('用户名或密码错误');
      } else if (error.code === 400) {
        message.error('请求参数错误，请检查输入');
      } else if (error.code === 500) {
        message.error('服务器内部错误，请稍后重试');
      } else if (error.code === 408) {
        message.error('请求超时，请检查网络连接');
      } else {
        message.error(error.message || '登录失败，请重试');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 退出登录
  const logout = useCallback(() => {
    authAPI.logout();
    // 注意：不删除记住密码的信息，让用户下次登录时可以选择是否记住
    setIsLoggedIn(false);
    setUserInfo(null);
    localStorage.removeItem('isLoggedIn');
    // 清除权限缓存
    sessionStorage.removeItem('rolePermissions');
    message.success('已退出登录');
    navigate('/login', { replace: true });
  }, [navigate]);

  // 检查是否需要登录
  const requireAuth = useCallback(() => {
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
      return false;
    }
    return true;
  }, [isLoggedIn, navigate]);

  const value: AuthContextType = {
    isLoggedIn,
    userInfo,
    loading,
    login,
    logout,
    requireAuth,
    refreshUserInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
